import React, { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ADMIN_AFF } from "@/constants/testIds";

function dollars(c) { return `$${((c || 0) / 100).toFixed(2)}`; }
const STATUSES = ["pending_review", "approved", "paid", "void"];

export default function AdminAffiliateLedger() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending_review");
  const [err, setErr] = useState("");

  const reload = async () => {
    setErr("");
    try {
      const url = filter ? `/admin/affiliate/ledger?status=${filter}&limit=300` : "/admin/affiliate/ledger?limit=300";
      const { data } = await api.get(url);
      setRows(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/affiliate/ledger/${id}`, { status }); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };

  return (
    <div className="space-y-6">
      <header className="fade-up">
        <div className="eyebrow">Affiliate admin</div>
        <h1 className="headline-xl">Commission ledger</h1>
        <p className="text-lifted-dim mt-2 text-sm max-w-2xl">
          Every commission event lands here as <code className="text-lifted-text">pending_review</code>. Approve or void.
          Approved entries become payout-eligible. <strong>Nothing auto-pays.</strong>
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`chip cursor-pointer ${filter === s ? "chip--green" : ""}`}>
            {s.replace("_", " ")}
          </button>
        ))}
        <button onClick={() => setFilter("")} className={`chip cursor-pointer ${filter === "" ? "chip--green" : ""}`}>all</button>
      </div>

      {err && <div className="text-rose-300 text-sm">{err}</div>}

      <div data-testid={ADMIN_AFF.ledger} className="glass-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Affiliate</th>
              <th className="px-4 py-3">Source / Order</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Pct</th>
              <th className="px-4 py-3">Commission</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={ADMIN_AFF.ledgerRow(r.id)} className="border-b border-white/5">
                <td className="px-4 py-3 text-xs text-lifted-dim">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3 text-xs text-lifted-dim break-all">{r.affiliate_id}</td>
                <td className="px-4 py-3">
                  <span className="chip">{r.order_source}</span>
                  <div className="text-xs text-lifted-mute mt-1 break-all">{r.order_id}</div>
                </td>
                <td className="px-4 py-3">{dollars(r.order_amount_cents)}</td>
                <td className="px-4 py-3">{r.commission_pct == null ? <span className="text-lifted-mute">—</span> : `${r.commission_pct}%`}</td>
                <td className="px-4 py-3 text-lifted-green">{dollars(r.commission_cents)}</td>
                <td className="px-4 py-3">
                  {{
                    pending_review: <span className="chip">Pending</span>,
                    approved: <span className="chip chip--green">Approved</span>,
                    paid: <span className="chip chip--purple">Paid</span>,
                    void: <span className="chip">Void</span>,
                  }[r.status]}
                </td>
                <td className="px-4 py-3 text-right">
                  {r.status === "pending_review" && (
                    <div className="inline-flex gap-2">
                      <button data-testid={ADMIN_AFF.approveBtn(r.id)} onClick={() => setStatus(r.id, "approved")} className="btn-lifted !py-1.5 !px-3 !text-[0.65rem]"><Check className="h-3 w-3" /> Approve</button>
                      <button data-testid={ADMIN_AFF.voidBtn(r.id)} onClick={() => setStatus(r.id, "void")} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem]"><X className="h-3 w-3" /> Void</button>
                    </div>
                  )}
                  {r.status === "approved" && (
                    <button onClick={() => setStatus(r.id, "void")} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem]"><X className="h-3 w-3" /> Void</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-lifted-mute">No ledger entries match the filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
